import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { fetchCarouselRequest } from "../../../../redux/slices/CarouselSlice";
import { carouselGradientBtn } from "../carouselStyles";

const EditCarousel = React.memo(({ open, onClose, formValues, onChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loadingSaving, setLoadingSaving] = useState(false);
  const dispatch = useDispatch();

  const locationLabel =
    formValues.locationLabel || formValues.carouselPosition || "";
  const locationKey = formValues.carouselPosition || "";

  const handleSubmit = async () => {
    setLoadingSaving(true);
    try {
      const payload = {
        edit: {
          id: formValues.id,
          location: locationKey,
          height: Number(formValues.carouselHeight),
          duration: Number(formValues.carouselDuration),
          enabled: formValues.enabled ?? true,
          shownavigator: formValues.shownavigator ?? true,
          edit: true,
        },
      };

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/carousel/savecorousel`,
        payload
      );

      toast.success("Carousel updated successfully");
      dispatch(fetchCarouselRequest());
      onClose();
    } catch (err) {
      console.error("Update failed", err);
      toast.error("Failed to update carousel");
    } finally {
      setLoadingSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 2 },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          pr: 6,
          fontSize: { xs: 18, sm: 20 },
        }}
      >
        Edit Carousel
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Stack spacing={2.5}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "#faf8ff",
              border: "1px solid #ece6f8",
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Location
            </Typography>
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{
                mt: 0.5,
                wordBreak: "break-word",
                color: "#7A3FFF",
              }}
            >
              {locationLabel}
            </Typography>
            {locationKey && locationKey !== locationLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5, wordBreak: "break-all" }}
              >
                Key: {locationKey}
              </Typography>
            )}
            {formValues.isSystem === false && (
              <Chip
                label="Custom"
                size="small"
                sx={{ mt: 1, bgcolor: "#ede7f6", color: "#7A3FFF" }}
              />
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                fullWidth
                required
                size="small"
                label="Height (px)"
                name="carouselHeight"
                value={formValues.carouselHeight ?? ""}
                onChange={onChange}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                fullWidth
                required
                size="small"
                label="Duration (seconds)"
                name="carouselDuration"
                value={formValues.carouselDuration ?? ""}
                onChange={onChange}
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
          "& > button": { width: { xs: "100%", sm: "auto" } },
        }}
      >
        <Button variant="outlined" onClick={onClose} disabled={loadingSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loadingSaving}
          sx={carouselGradientBtn}
        >
          {loadingSaving ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: "inherit" }} />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default EditCarousel;
